import { useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/site/Reveal";
import { CtaButton } from "@/components/site/CtaButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUDED = [
  "A full diagnosis of where your week actually goes — every hour accounted for.",
  "A written prescription: at least 5 tools you can self-install for free.",
  "Plain-English setup notes for each fix — no jargon, no upsells.",
  "The guarantee: find 5 hours a week, or the checkup is free.",
];

export default function BookCheckup() {
  const [status, setStatus] = useState("idle"); // idle | loading | error
  const [error, setError] = useState("");

  const startCheckout = async () => {
    setStatus("loading");
    setError("");
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "checkup_onetime",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setStatus("error");
      setError("The till is having a moment. Please try again.");
    }
  };

  return (
    <div data-testid="book-checkup-page">
      <section className="shell min-h-screen flex flex-col justify-center py-40">
        <Reveal>
          <span className="eyebrow">Book Your Checkup</span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-[#f5f3ee] max-w-3xl">
            The AI Efficiency Checkup
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#a7a7a2] max-w-xl leading-relaxed">
            One visit. A complete diagnosis of your operations, a prescription you can
            fill yourself, and a guarantee in writing: I find you 5 hours a week, or it's free.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_360px] items-start">
            <ul className="space-y-5" data-testid="checkup-included-list">
              {INCLUDED.map((item, i) => (
                <li key={i} className="flex gap-4 border-b border-white/10 pb-5">
                  <span className="font-mono text-[11px] tracking-[0.18em] text-[#5CCBF5] pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-base text-[#f5f3ee] leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            <div className="border border-white/10 bg-white/[0.02] p-8" data-testid="checkout-card">
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#a7a7a2]">
                One-time visit fee
              </span>
              <div className="mt-3 text-5xl font-medium text-[#f5f3ee]" data-testid="checkout-price">
                $999
              </div>
              <p className="mt-3 text-sm text-[#a7a7a2] leading-relaxed">
                Secure payment by card. 5 hours a week found, or every cent back.
              </p>
              <div className="mt-8">
                <CtaButton
                  onClick={startCheckout}
                  testid="checkout-pay-button"
                  className={status === "loading" ? "opacity-60 pointer-events-none" : ""}
                >
                  {status === "loading" ? "Preparing checkout…" : "Pay & Book — $999"}
                </CtaButton>
              </div>
              {error && (
                <p className="mt-4 text-sm text-red-400" data-testid="checkout-error">{error}</p>
              )}
              <p className="mt-6 text-[#5c5c58] text-xs leading-relaxed">
                Payment is processed securely by Stripe — card details never touch this site. Your booking
                details are used only to schedule your call. See the{" "}
                <a href="/privacy" className="text-[#a7a7a2] underline underline-offset-4 hover:text-[#5CCBF5]">Privacy Policy</a>{" "}
                and{" "}
                <a href="/terms" className="text-[#a7a7a2] underline underline-offset-4 hover:text-[#5CCBF5]">Terms of Use</a>.
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
